import React from 'react';
import { Link } from 'react-router-dom';
import { Robot, ListChecks, WarningCircle, ArrowRight } from '@phosphor-icons/react';
import { ProgressRing } from '../../design-system/components';

export default function ExecutivePostureRow({ posture }) {
  const { readiness, executionRisk, evidencePosture, topRisks, keyDecisions, activeAgents } = posture || {};

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6" data-testid="executive-posture-row">
      {/* 1. Decision Readiness */}
      <div className="flex flex-col justify-between rounded-xl border border-[var(--line)] bg-[var(--surface-card)] p-4 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--ink-400)]">
              Decision Readiness
            </span>
            <div className="mt-2 flex items-center gap-2">
              <span className="font-heading text-lg font-bold text-[var(--ink-950)]">
                {readiness?.stateLabel || 'Unknown'}
              </span>
            </div>
          </div>
          <ProgressRing
            value={null}
            stateLabel={readiness?.stateLabel || 'Unknown'}
            tone={readiness?.tone || 'neutral'}
            size={40}
            testId="ring-readiness"
          />
        </div>
        <span className="mt-3 text-[11px] text-[var(--ink-400)]">
          Conditions validated
        </span>
      </div>

      {/* 2. Execution Risk */}
      <div className="flex flex-col justify-between rounded-xl border border-[var(--line)] bg-[var(--surface-card)] p-4 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--ink-400)]">
              Execution Risk
            </span>
            <div className="mt-2 flex items-center gap-2">
              <span className="font-heading text-lg font-bold text-[var(--ink-950)]">
                {executionRisk?.stateLabel || 'Unknown'}
              </span>
            </div>
          </div>
          <ProgressRing
            value={null}
            stateLabel={executionRisk?.stateLabel || 'Unknown'}
            tone={executionRisk?.tone || 'neutral'}
            size={40}
            testId="ring-execution-risk"
          />
        </div>
        <span className="mt-3 text-[11px] text-[var(--ink-400)]">
          Authoritative risk posture
        </span>
      </div>

      {/* 3. Evidence Posture */}
      <div className="flex flex-col justify-between rounded-xl border border-[var(--line)] bg-[var(--surface-card)] p-4 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--ink-400)]">
              Evidence Posture
            </span>
            <div className="mt-2 flex items-center gap-2">
              <span className="font-heading text-lg font-bold text-[var(--ink-950)]">
                {evidencePosture?.formattedValue || '--'}
              </span>
              <span className="text-xs text-[var(--ink-400)]">
                ({evidencePosture?.stateLabel || 'Missing'})
              </span>
            </div>
          </div>
          <ProgressRing
            value={evidencePosture?.value}
            stateLabel={evidencePosture?.stateLabel || 'Missing'}
            tone={evidencePosture?.tone || 'neutral'}
            size={40}
            testId="ring-evidence-posture"
          />
        </div>
        <span className="mt-3 text-[11px] text-[var(--ink-400)]">
          Confidence weighted
        </span>
      </div>

      {/* 4. Top Execution Risks */}
      <div className="flex flex-col justify-between rounded-xl border border-[var(--line)] bg-[var(--surface-card)] p-4 shadow-sm">
        <div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--ink-400)]">
              Top Execution Risks
            </span>
            <Link
              to="/worktree"
              className="text-[11px] font-semibold text-slate-700 hover:text-slate-950"
              aria-label="View all risks in Worktree"
            >
              <ArrowRight size={12} />
            </Link>
          </div>
          <div className="mt-2 space-y-1.5">
            {topRisks && topRisks.length > 0 ? (
              topRisks.slice(0, 2).map((r) => (
                <div key={r.id} className="flex items-start justify-between gap-1 text-xs">
                  <span className="truncate font-medium text-slate-900" title={r.title}>
                    {r.title}
                  </span>
                  <span
                    className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${
                      r.severity === 'CRITICAL'
                        ? 'bg-rose-100 text-rose-800'
                        : r.severity === 'HIGH'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {r.severity}
                  </span>
                </div>
              ))
            ) : (
              <span className="text-xs text-slate-500">0 Active Risks</span>
            )}
          </div>
        </div>
        <span className="mt-3 text-[11px] text-[var(--ink-400)]">
          {topRisks?.length || 0} evaluated risk(s)
        </span>
      </div>

      {/* 5. Key Decisions */}
      <div className="flex flex-col justify-between rounded-xl border border-[var(--line)] bg-[var(--surface-card)] p-4 shadow-sm">
        <div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--ink-400)]">
              Key Decisions
            </span>
            <Link
              to="/work"
              className="text-[11px] font-semibold text-slate-700 hover:text-slate-950"
              aria-label="View decisions in Work surface"
            >
              <ArrowRight size={12} />
            </Link>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <div>
              <span className="font-heading text-base font-bold text-slate-900">
                {keyDecisions?.pending || 0}
              </span>
              <span className="block text-[10px] text-slate-500">Pending</span>
            </div>
            <div>
              <span
                className={`font-heading text-base font-bold ${
                  (keyDecisions?.overdue || 0) > 0 ? 'text-rose-600' : 'text-slate-900'
                }`}
              >
                {keyDecisions?.overdue || 0}
              </span>
              <span className="block text-[10px] text-slate-500">Overdue</span>
            </div>
          </div>
        </div>
        <span className="mt-3 text-[11px] text-[var(--ink-400)]">
          {keyDecisions?.approved || 0} approved / {keyDecisions?.total || 0} total
        </span>
      </div>

      {/* 6. Active AI Agents */}
      <div className="flex flex-col justify-between rounded-xl border border-[var(--line)] bg-[var(--surface-card)] p-4 shadow-sm">
        <div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--ink-400)]">
              Active AI Agents
            </span>
            <Robot size={14} className="text-slate-400" aria-hidden />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className="inline-block h-2 w-2 rounded-full bg-slate-300" aria-hidden />
            <span className="font-heading text-sm font-semibold text-slate-700">
              {activeAgents?.label || 'Runtime Inactive'}
            </span>
          </div>
        </div>
        <span className="mt-3 text-[11px] text-[var(--ink-400)]">
          Gate 10 Governed Runtime
        </span>
      </div>
    </div>
  );
}
